import { useId, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { useMutation, useQuery } from "@apollo/client/react";
import { Send } from "lucide-react";
import { Button, Card, Checkbox, cn, Eyebrow, Heading, Input, Label, Skeleton, Text, Textarea } from "@athena/ui";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import {
    ClientBroadcastAudienceDocument,
    SendClientBroadcastDocument,
    SendClientChatBroadcastDocument,
} from "@/graphql/clients.generated";
import { AudienceFilters } from "./components/AudienceFilters";
import { LeftOutList, type LeftOutGroup } from "./components/LeftOutList";
import { RecipientList } from "./components/RecipientList";
import {
    audienceFilterSummary,
    filterChoices,
    isFiltered,
    matchesAudienceFilter,
    type AudienceFilter,
} from "./helpers/audienceFilter";
import { fullName, toClientRecord, type ClientRecord } from "./helpers/client";

/** The composer's field labels: small mono caps, as the mockup's SUBJECT and BODY. */
const FIELD_LABEL = "text-text-eyebrow font-mono text-xs font-medium tracking-widest uppercase";

/** Which channel is ticked, if either: the i18n context that picks its wording. */
type Channel = "email" | "chat" | "none";

/** How many names an in-app chat's To line spells out before "and N others". */
const TO_NAMES_SHOWN = 3;

/**
 * One plain-text message to every alum at once (ATH-169, ATH-188): by email or in the app, one or the
 * other. Its body, who it reaches, and a checkbox per recipient to leave someone out of this one.
 *
 * An email has a subject. An in-app chat has none — it lands in each alum's own chat with their
 * coach, from the coordinator — so in its place the composer says who it is going to. It reaches
 * anyone with an account who is not do not contact: an email unsubscribe is about email.
 *
 * Ticking one channel clears the other, and either can be cleared: then it reaches no one until one
 * is picked.
 *
 * Filters by location, sex and discharge date narrow it to part of the alumni — one program's, say.
 * They apply to every list and count on the page, so each alum is in exactly one of: receiving,
 * unchecked, left out by the server's rule, or outside the filters.
 *
 * Who it reaches is the server's answer (`clientBroadcastAudience`, `clientChatBroadcastAudience`), by
 * the same rules the sends use, so the count confirmed is the count that gets it. Each send names
 * exactly the people still checked that it reaches; the server can only drop someone, never add.
 *
 * The mockup's text-message channel, templates, merge fields, attachments and segments are later
 * tickets.
 */
export function BroadcastPage() {
    const { t } = useTranslation();
    const ids = useId();
    const [
        subject,
        setSubject,
    ] = useState<string>("");
    const [
        body,
        setBody,
    ] = useState<string>("");
    const [
        excluded,
        setExcluded,
    ] = useState<ReadonlySet<string>>(new Set<string>());
    const [
        confirming,
        setConfirming,
    ] = useState<boolean>(false);
    const [
        filter,
        setFilter,
    ] = useState<AudienceFilter>({});
    const [
        channel,
        setChannel,
    ] = useState<Channel>("email");
    const { data, loading, error, refetch } = useQuery(ClientBroadcastAudienceDocument);
    const [
        sendBroadcast,
        { loading: sendingEmail },
    ] = useMutation(SendClientBroadcastDocument);
    const [
        sendChatBroadcast,
        { loading: sendingChat },
    ] = useMutation(SendClientChatBroadcastDocument);

    const everyone = useMemo(() => {
        const lists = data?.clientBroadcastAudience;
        const chat = data?.clientChatBroadcastAudience;
        return {
            recipients: (lists?.recipients ?? []).map(toClientRecord),
            doNotContact: (lists?.doNotContact ?? []).map(toClientRecord),
            unsubscribed: (lists?.unsubscribed ?? []).map(toClientRecord),
            noEmail: (lists?.noEmail ?? []).map(toClientRecord),
            // Do not contact is the same people for both, so the email's list stands for it.
            chatRecipients: (chat?.recipients ?? []).map(toClientRecord),
            noAccount: (chat?.noAccount ?? []).map(toClientRecord),
        };
    }, [data]);
    const allAlumni = useMemo(
        () => [
            ...everyone.recipients,
            ...everyone.doNotContact,
            ...everyone.unsubscribed,
            ...everyone.noEmail,
        ],
        [everyone],
    );
    // Choices come from everyone, so picking one never takes the others away.
    const locations = useMemo(() => filterChoices(allAlumni.map((client) => client.location)), [allAlumni]);
    const sexes = useMemo(() => filterChoices(allAlumni.map((client) => client.sex)), [allAlumni]);

    const audience = useMemo(() => {
        const matching = (clients: ClientRecord[]) => clients.filter((client) => matchesAudienceFilter(client, filter));
        return {
            recipients: matching(everyone.recipients),
            doNotContact: matching(everyone.doNotContact),
            unsubscribed: matching(everyone.unsubscribed),
            noEmail: matching(everyone.noEmail),
            chatRecipients: matching(everyone.chatRecipients),
            noAccount: matching(everyone.noAccount),
        };
    }, [
        everyone,
        filter,
    ]);

    const emailOn = channel === "email";
    const chatOn = channel === "chat";
    const reachable = emailOn ? audience.recipients : chatOn ? audience.chatRecipients : [];
    // Who the ticked channel leaves out, by why. The app needs only an account, so the unsubscribed
    // and those with no email are reached there. With nothing ticked there is no why: it reaches no
    // one until a channel is picked.
    const leftOutGroups: LeftOutGroup[] = emailOn
        ? [
              { label: t("admin.alumni.broadcast.doNotContact"), clients: audience.doNotContact },
              { label: t("admin.alumni.broadcast.unsubscribed"), clients: audience.unsubscribed },
              { label: t("admin.alumni.broadcast.noEmail"), clients: audience.noEmail },
          ]
        : chatOn
          ? [
                { label: t("admin.alumni.broadcast.doNotContact"), clients: audience.doNotContact },
                { label: t("admin.alumni.broadcast.noAccount"), clients: audience.noAccount },
            ]
          : [];

    const chosen = reachable.filter((client) => !excluded.has(client.id));
    const recipients = chosen.length;
    const removed = reachable.length - recipients;
    const leftOut = leftOutGroups.reduce((total, group) => total + group.clients.length, 0);
    const filtered = isFiltered(filter);
    // The email lists hold everyone once, whichever channel is ticked.
    const matchingFilters =
        audience.recipients.length +
        audience.doNotContact.length +
        audience.unsubscribed.length +
        audience.noEmail.length;
    const outsideFilters = allAlumni.length - matchingFilters;
    const failed = Boolean(error) && !data;
    const canSend = Boolean(data) && recipients > 0 && (!emailOn || subject.trim() !== "") && body.trim() !== "";

    // An in-app chat's To line: the first few checked, by name as the list shows them, then a count.
    const byName = [...chosen].sort((a, b) => fullName(a).localeCompare(fullName(b)));
    const toNames = byName
        .slice(0, TO_NAMES_SHOWN)
        .map((client) => fullName(client))
        .join(", ");
    const toOthers = byName.length - TO_NAMES_SHOWN;
    const toLine =
        recipients === 0
            ? t("admin.alumni.broadcast.toNobody")
            : toOthers > 0
              ? t("admin.alumni.broadcast.toSome", { names: toNames, count: toOthers })
              : toNames;

    const toggle = (clientIds: string[], included: boolean) =>
        setExcluded((current) => {
            const next = new Set(current);
            for (const clientId of clientIds) {
                if (included) next.delete(clientId);
                else next.add(clientId);
            }
            return next;
        });

    /** Emails everyone checked. True when any of it went. */
    const sendEmail = async (): Promise<boolean> => {
        let result;
        try {
            const response = await sendBroadcast({
                variables: {
                    input: {
                        subject: subject.trim(),
                        body: body.trim(),
                        recipientClientIds: chosen.map((client) => client.id),
                    },
                },
            });
            result = response.data?.sendClientBroadcast;
        } catch {
            toast.error(t("admin.alumni.broadcast.sendFailed"));
            return false;
        }
        if (!result) return false;

        const skipped =
            result.skippedCount > 0 ? t("admin.alumni.broadcast.skipped", { count: result.skippedCount }) : undefined;
        if (result.sentCount === 0) {
            toast.error(t("admin.alumni.broadcast.sentNone"));
            return false;
        }
        if (result.sentCount < result.recipientCount) {
            toast.warning(
                t("admin.alumni.broadcast.sentSome", {
                    sent: result.sentCount,
                    count: result.recipientCount,
                    failed: result.recipientCount - result.sentCount,
                }),
                { description: skipped },
            );
        } else {
            toast.success(t("admin.alumni.broadcast.sentAll", { count: result.sentCount }), { description: skipped });
        }
        return true;
    };

    /** Posts in the chat of everyone checked. True when it went. */
    const sendChat = async (): Promise<boolean> => {
        let result;
        try {
            const response = await sendChatBroadcast({
                variables: {
                    input: { body: body.trim(), recipientClientIds: chosen.map((client) => client.id) },
                },
            });
            result = response.data?.sendClientChatBroadcast;
        } catch {
            toast.error(t("admin.alumni.broadcast.chatSendFailed"));
            return false;
        }
        if (!result) return false;
        toast.success(t("admin.alumni.broadcast.chatSent", { count: result.recipientCount }), {
            description:
                result.skippedCount > 0
                    ? t("admin.alumni.broadcast.chatSkipped", { count: result.skippedCount })
                    : undefined,
        });
        return true;
    };

    const send = async () => {
        const went = emailOn ? await sendEmail() : chatOn ? await sendChat() : false;
        // Nothing went: the message, and the confirmation, stay to try again.
        if (!went) return;
        setConfirming(false);
        // Sent, so a second press would send it again: start over, from who can be reached now.
        setSubject("");
        setBody("");
        setExcluded(new Set<string>());
        setFilter({});
        void refetch();
    };

    const filterSummary = audienceFilterSummary(filter, t);

    const confirmDescription = [
        t("admin.alumni.broadcast.confirmDescription", {
            context: channel,
            count: recipients,
            subject: subject.trim(),
        }),
        filtered
            ? t("admin.alumni.broadcast.confirmFiltered", { count: outsideFilters, filters: filterSummary })
            : null,
        leftOut > 0 ? t("admin.alumni.broadcast.confirmLeftOut", { context: channel, count: leftOut }) : null,
        removed > 0 ? t("admin.alumni.broadcast.confirmRemoved", { count: removed }) : null,
    ]
        .filter(Boolean)
        .join(" ");

    return (
        // On a wide screen the page is exactly the window, less AlumniLayout's 3rem (h-12) header, so it
        // never scrolls: the recipient list scrolls inside its card instead.
        <div className="flex flex-col gap-6 px-4 py-6 md:px-10 md:py-8 lg:h-[calc(100svh-3rem)]">
            <header className="max-w-2xl">
                <Heading level={2} as="h1">
                    {t("admin.alumni.broadcast.title")}
                </Heading>
                <Text size="sm" muted className="mt-1">
                    {t("admin.alumni.broadcast.description")}
                </Text>
            </header>

            {/* As the mockup: who it goes to on the left, the composer, summary and Send on the right. */}
            <div className="grid items-start gap-6 lg:min-h-0 lg:flex-1 lg:grid-cols-[22rem_minmax(0,1fr)] lg:grid-rows-[minmax(0,1fr)] lg:items-stretch">
                <Card
                    width="full"
                    role="region"
                    aria-labelledby={`${ids}-compose`}
                    className="lg:col-start-2 lg:row-start-1 lg:max-h-full lg:self-start lg:overflow-y-auto"
                >
                    <div className="px-6 pt-6 pb-5">
                        <Heading level={4} as="h2" id={`${ids}-compose`}>
                            {t("admin.alumni.broadcast.composeTitle")}
                        </Heading>
                        <Text size="sm" muted className="mt-1">
                            {t("admin.alumni.broadcast.composeDescription")}
                        </Text>
                    </div>
                    {/* How it goes first: it decides who it reaches, and whether it has a subject or a To line. */}
                    <div
                        role="group"
                        aria-labelledby={`${ids}-channels`}
                        className="border-border flex flex-col gap-3 border-t px-6 py-6"
                    >
                        <div>
                            <h3 id={`${ids}-channels`} className={FIELD_LABEL}>
                                {t("admin.alumni.broadcast.channels.title")}
                            </h3>
                            <Text size="xs" muted className="mt-1">
                                {t("admin.alumni.broadcast.channels.description")}
                            </Text>
                        </div>
                        <div className="flex flex-wrap gap-x-6 gap-y-2">
                            <label className="flex cursor-pointer items-center gap-2">
                                {/* One or the other: ticking one clears the other, and either can be cleared. */}
                                <Checkbox
                                    checked={emailOn}
                                    onCheckedChange={(checked) => setChannel(checked === true ? "email" : "none")}
                                />
                                <Text as="span" size="sm" weight="medium">
                                    {t("admin.alumni.broadcast.channels.email")}
                                </Text>
                            </label>
                            <label className="flex cursor-pointer items-center gap-2">
                                <Checkbox
                                    checked={chatOn}
                                    onCheckedChange={(checked) => setChannel(checked === true ? "chat" : "none")}
                                />
                                <Text as="span" size="sm" weight="medium">
                                    {t("admin.alumni.broadcast.channels.chat")}
                                </Text>
                            </label>
                        </div>
                        {channel === "none" ? (
                            <Text size="sm" className="text-destructive" role="status">
                                {t("admin.alumni.broadcast.channels.none")}
                            </Text>
                        ) : null}
                    </div>
                    {/* Who it is for first, as an email's To comes before its subject. */}
                    <section
                        aria-labelledby={`${ids}-filters`}
                        className="border-border flex flex-col gap-3 border-t px-6 py-6"
                    >
                        <div>
                            <h3 id={`${ids}-filters`} className={FIELD_LABEL}>
                                {t("admin.alumni.broadcast.filters.title")}
                            </h3>
                            <Text size="xs" muted className="mt-1">
                                {t("admin.alumni.broadcast.filters.description")}
                            </Text>
                        </div>
                        <AudienceFilters filter={filter} onChange={setFilter} locations={locations} sexes={sexes} />
                    </section>
                    <div className="border-border flex flex-col gap-6 border-t px-6 py-6">
                        {/* A chat has no subject: in its place, who it is going to. */}
                        {chatOn ? (
                            <div className="flex flex-col gap-3">
                                <span id={`${ids}-to`} className={FIELD_LABEL}>
                                    {t("admin.alumni.broadcast.toLabel")}
                                </span>
                                {/* Read-only, and looks it: filled and dashed rather than an input's white box.
                                    Who it goes to is changed under Recipients, not here. */}
                                <p
                                    aria-labelledby={`${ids}-to`}
                                    className="bg-muted/60 border-border text-text-muted min-h-12 cursor-default rounded-xl border border-dashed px-5 py-3 text-base"
                                >
                                    {toLine}
                                </p>
                            </div>
                        ) : null}
                        {emailOn ? (
                            <div className="flex flex-col gap-3">
                                <Label htmlFor={`${ids}-subject`} className={FIELD_LABEL}>
                                    {t("admin.alumni.broadcast.subjectLabel")}
                                </Label>
                                <Input
                                    id={`${ids}-subject`}
                                    value={subject}
                                    onChange={(event) => setSubject(event.target.value)}
                                    placeholder={t("admin.alumni.broadcast.subjectPlaceholder")}
                                    className="h-12 rounded-xl px-5 text-base"
                                />
                            </div>
                        ) : null}
                        <div className="flex flex-col gap-3">
                            <Label htmlFor={`${ids}-body`} className={FIELD_LABEL}>
                                {t("admin.alumni.broadcast.bodyLabel")}
                            </Label>
                            <Textarea
                                id={`${ids}-body`}
                                value={body}
                                onChange={(event) => setBody(event.target.value)}
                                placeholder={t("admin.alumni.broadcast.bodyPlaceholder")}
                                className="min-h-64 resize-y rounded-xl px-5 py-4 text-base leading-relaxed"
                            />
                            <Text size="xs" muted>
                                {t("admin.alumni.broadcast.plainTextNote")}
                            </Text>
                        </div>
                    </div>
                    {/* Who it reaches and Send, at the foot of the message they apply to. */}
                    <section
                        aria-labelledby={`${ids}-audience`}
                        className="border-border bg-muted/40 flex flex-col gap-4 border-t px-6 py-5 sm:flex-row sm:items-center sm:justify-between"
                    >
                        <div className="flex min-w-0 flex-col gap-1">
                            <Eyebrow id={`${ids}-audience`}>{t("admin.alumni.broadcast.audienceTitle")}</Eyebrow>
                            {failed ? (
                                <Text size="sm" muted>
                                    {t("admin.alumni.broadcast.loadFailed")}
                                </Text>
                            ) : loading && !data ? (
                                <Skeleton className="h-8 w-64" aria-label={t("admin.alumni.broadcast.loading")} />
                            ) : (
                                <div className="flex flex-wrap items-baseline gap-x-6 gap-y-1">
                                    <div className="flex items-baseline gap-2">
                                        <span className="font-display text-text-heading text-2xl leading-tight font-semibold tracking-tight">
                                            {recipients}
                                        </span>
                                        <Text as="span" size="sm" muted>
                                            {t("admin.alumni.broadcast.willReceive", {
                                                context: channel,
                                                count: recipients,
                                            })}
                                        </Text>
                                    </div>
                                    <dl className="flex flex-wrap gap-x-4 gap-y-1 text-sm">
                                        <div className="flex gap-1.5">
                                            <dt className="text-text-muted">{t("admin.alumni.broadcast.allAlumni")}</dt>
                                            <dd>{allAlumni.length}</dd>
                                        </div>
                                        {filtered ? (
                                            <div className="flex gap-1.5">
                                                <dt className="text-text-muted">
                                                    {t("admin.alumni.broadcast.outsideFilters")}
                                                </dt>
                                                <dd>{outsideFilters}</dd>
                                            </div>
                                        ) : null}
                                        <div className="flex gap-1.5">
                                            <dt className="text-text-muted">
                                                {t("admin.alumni.broadcast.doNotContact")}
                                            </dt>
                                            <dd>{audience.doNotContact.length}</dd>
                                        </div>
                                        {!chatOn && audience.unsubscribed.length > 0 ? (
                                            <div className="flex gap-1.5">
                                                <dt className="text-text-muted">
                                                    {t("admin.alumni.broadcast.unsubscribed")}
                                                </dt>
                                                <dd>{audience.unsubscribed.length}</dd>
                                            </div>
                                        ) : null}
                                        {!chatOn && audience.noEmail.length > 0 ? (
                                            <div className="flex gap-1.5">
                                                <dt className="text-text-muted">
                                                    {t("admin.alumni.broadcast.noEmail")}
                                                </dt>
                                                <dd>{audience.noEmail.length}</dd>
                                            </div>
                                        ) : null}
                                        {chatOn && audience.noAccount.length > 0 ? (
                                            <div className="flex gap-1.5">
                                                <dt className="text-text-muted">
                                                    {t("admin.alumni.broadcast.noAccount")}
                                                </dt>
                                                <dd>{audience.noAccount.length}</dd>
                                            </div>
                                        ) : null}
                                        <div className="flex gap-1.5">
                                            <dt className="text-text-muted">{t("admin.alumni.broadcast.removed")}</dt>
                                            {/* Red while anyone is unchecked: a reminder, in case one was by accident. */}
                                            <dd className={cn(removed > 0 && "text-destructive font-semibold")}>
                                                {removed}
                                            </dd>
                                        </div>
                                    </dl>
                                </div>
                            )}
                        </div>
                        <Button
                            className="shrink-0 sm:w-auto sm:max-w-none"
                            disabled={!canSend}
                            onClick={() => setConfirming(true)}
                        >
                            <Send aria-hidden />
                            {t("admin.alumni.broadcast.send", { count: recipients })}
                        </Button>
                    </section>
                </Card>

                {/* Filters and who is left out take what they need; the recipients fill the rest, but never
                    shrink below a usable list. On a short window the column scrolls instead. */}
                <div className="flex flex-col gap-6 lg:col-start-1 lg:row-start-1 lg:min-h-0 lg:overflow-y-auto">
                    {leftOut > 0 ? (
                        <Card width="full" role="region" aria-labelledby={`${ids}-left-out`} className="shrink-0">
                            <div className="px-5 pt-5 pb-4">
                                <Heading level={4} as="h2" id={`${ids}-left-out`}>
                                    {t("admin.alumni.broadcast.leftOutTitle")}
                                </Heading>
                                <Text size="sm" muted className="mt-1">
                                    {t("admin.alumni.broadcast.leftOutDescription", { context: channel })}
                                </Text>
                            </div>
                            <div className="border-border border-t px-5 py-4">
                                <LeftOutList groups={leftOutGroups} />
                            </div>
                        </Card>
                    ) : null}

                    <Card
                        width="full"
                        role="region"
                        aria-labelledby={`${ids}-recipients`}
                        className="flex min-h-0 flex-1 flex-col lg:min-h-96"
                    >
                        <div className="px-5 pt-5 pb-4">
                            <Heading level={4} as="h2" id={`${ids}-recipients`}>
                                {t("admin.alumni.broadcast.recipientsTitle")}
                            </Heading>
                            <Text size="sm" muted className="mt-1">
                                {t("admin.alumni.broadcast.recipientsDescription")}
                            </Text>
                        </div>
                        <div className="border-border flex min-h-0 flex-1 flex-col border-t px-5 py-4">
                            {failed ? (
                                <Text size="sm" muted>
                                    {t("admin.alumni.broadcast.loadFailed")}
                                </Text>
                            ) : (
                                <RecipientList
                                    recipients={reachable}
                                    emptyMessage={
                                        channel === "none" ? t("admin.alumni.broadcast.channels.none") : undefined
                                    }
                                    excluded={excluded}
                                    onToggle={toggle}
                                    loading={loading && !data}
                                />
                            )}
                        </div>
                    </Card>
                </div>
            </div>

            <ConfirmDialog
                open={confirming}
                onOpenChange={setConfirming}
                title={t("admin.alumni.broadcast.confirmTitle", { context: channel })}
                description={confirmDescription}
                confirmLabel={t("admin.alumni.broadcast.send", { count: recipients })}
                loadingLabel={t("admin.alumni.broadcast.sending")}
                loading={sendingEmail || sendingChat}
                onConfirm={send}
            />
        </div>
    );
}
